import { useImperativeHandle, forwardRef, useState } from "react";
import { CircleAlert } from "lucide-react";
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";

interface DeleteModalProps {
  onDeleteClick: () => void;
  confirmText: string;
  deleteText: string;
  cancelText: string;
}

export interface DeleteModalHandle {
  open: () => void;
  close: () => void;
}

const DeleteModal = forwardRef<DeleteModalHandle, DeleteModalProps>(
  ({ onDeleteClick, confirmText, deleteText, cancelText }, ref) => {
    const [isOpen, setIsOpen] = useState(false);

    useImperativeHandle(ref, () => ({
      open: () => setIsOpen(true),
      close: () => setIsOpen(false),
    }));

    return (
      <AlertDialog open={isOpen} onOpenChange={setIsOpen}>
        <AlertDialogTrigger asChild>
          <button className="hidden" />
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <Button
              variant="ghost"
              onClick={() => setIsOpen(false)}
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 text-lg"
            >
              <span className="sr-only">Close</span>
              &times;
            </Button>
            <div className="flex justify-center mb-4">
              <div className="text-warning p-2 rounded-full">
                <CircleAlert className="w-6 h-6" />
              </div>
            </div>
            <AlertDialogTitle className="text-center text-lg font-medium">
              {confirmText}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-center text-gray-600">
              Are you sure you want to delete this item?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex justify-center gap-4">
            <AlertDialogAction variant="destructive" asChild>
              <Button variant="destructive" onClick={onDeleteClick}>
                {deleteText}
              </Button>
            </AlertDialogAction>
            <AlertDialogCancel asChild>
              <Button variant="outline">{cancelText}</Button>
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }
);

export default DeleteModal;
